import { useRealtimeDatabase } from './useRealtimeDatabase';
import useApp from 'core/hooks/useApp';
import useWG from 'core/hooks/useWG';
import sysConfig from 'service/sysConfig';
import { ref, computed } from 'vue';
import { UserInfoType } from 'types/appConfigType';

/**
 * 交易监听 Hook
 * 用于监听实时数据库中的交易数据并处理
 */
export function useTransactionListener() {
    const { userStore, appStore } = useApp();
    const { sendFirstRecharge, sendRecharge } = useWG();
    const { listenToData } = useRealtimeDatabase();

    // 获取系统和用户数据
    const systemData = computed(() => appStore.systemData);
    const userinfo = computed(() => userStore.userInfo);
    const isLogin = computed(() => userStore.IsLogin);

    // 数据监听结果
    const data = ref<any>(null);
    const loading = ref(true);
    const error = ref<Error | null>(null);

    // 数据监听器引用
    let dataListener = ref<any>(null);

    /**
     * 开始监听指定路径的交易数据
     * @param customPath 自定义监听路径，不传则使用默认路径
     * @returns 监听状态
     */
    function startListening(customPath?: string) {
        // 如果用户未登录，则不启动监听
        if (!isLogin.value) return { success: false, message: '用户未登录' };

        // 确定监听路径
        const defaultPath = `${systemData.value.aliasNum}/${userinfo.value?.uid || ''}`;
        const path = customPath || defaultPath;

        console.log(`开始监听路径: ${path}`);
        loading.value = true;
        error.value = null;

        // 如果已有监听，先取消
        if (dataListener.value && dataListener.value.unsubscribe) {
            dataListener.value.unsubscribe();
        }

        // 使用 Hook 监听数据
        dataListener.value = listenToData(path, (newData: any) => {
            // 回调处理数据
            processTransactions(newData);
        });

        // 更新状态
        if (dataListener.value) {
            data.value = dataListener.value.data;
            loading.value = dataListener.value.loading;
            error.value = dataListener.value.error;
        }

        return { success: true, message: `成功监听路径: ${path}` };
    }

    /**
     * 停止监听交易数据
     */
    function stopListening() {
        if (dataListener.value && dataListener.value.unsubscribe) {
            dataListener.value.unsubscribe();
            dataListener.value = null;
            console.log('已停止监听');
            return true;
        }
        return false;
    }

    /**
     * 处理交易数据，提取所有交易记录并处理
     * @param rawData 原始数据
     */
    async function processTransactions(rawData: any) {
        if (!rawData) return;

        // 递归处理嵌套对象，查找包含交易信息的对象
        async function traverse(obj: any, path: string[] = []) {
            if (!obj || typeof obj !== 'object') return;

            // 检查这个对象是否是一个交易记录
            try {
                if (obj.eventName && obj.amount && obj.username) {
                    // 获取当前记录的ID (最后一个路径片段)
                    const recordId = path.length > 0 ? path[path.length - 1] : '';

                    const data = {
                        id: recordId,
                        username: obj.username,
                        amount: obj.amount,
                        eventName: obj.eventName,
                        timestamp: obj.timestamp,
                        uid: obj.uid,
                    };
                    console.log(data, 'data');

                    let ok = false;

                    if (obj.eventName === 'firstrecharge') {
                        ok = sendFirstRecharge({
                            amount: data.amount,
                            uid: userinfo.value?.uid,
                            phone: userinfo.value?.id,
                        });
                        console.log(ok, '首充是否ok');
                    } else if (obj.eventName === 'recharge') {
                        ok = sendRecharge({
                            amount: data.amount,
                            uid: userinfo.value?.uid,
                            phone: userinfo.value?.id,
                        });
                        console.log(ok, '复充是否ok');
                    }

                    if (ok) {
                        // 请求接口标记
                        console.log('请求接口标记');
                        await sysConfig.delAfMessage({
                            id: data.id,
                        });
                    }
                    return;
                }
            } catch (error) {
                console.error('处理交易数据时发生错误:', error);
            }

            // 否则递归处理子对象
            for (const key in obj) {
                await traverse(obj[key], [...path, key]);
            }
        }

        await traverse(rawData);
    }

    return {
        startListening,
        stopListening,
        data,
        loading,
        error,
    };
}
